import { Transition } from '@headlessui/react'
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { Fragment } from 'react'
import { BalanceBar } from '../BalanceBar'
import { GenerateButton } from '../GenerateButton'
import { ProConList } from '../List'
import { type ListItemData, type ListSide } from '../ListItem'

export interface OptionCardCallbacks {
  onEditTitle: (id: string, label: string) => void
  onDeleteOption: (id: string) => void
  onAddReason: (optionId: string, side: ListSide, label: string) => void
  onEditReason: (id: string, label: string) => void
  onDeleteReason: (id: string) => void
  onToggleReasonWeight: (id: string, currentWeight: number) => void
}

interface OptionCardProps extends OptionCardCallbacks {
  id: string
  label: string
  proReasons: ListItemData[]
  conReasons: ListItemData[]
  isTouchDevice?: boolean
  autoFocusPro?: boolean
  isGenerating?: boolean
  generationError?: string | null
  onDismissError?: () => void
  onGenerate?: () => void
  showGenerateButton?: boolean
  isGenerateDisabled?: boolean
  titlePlaceholder?: string
  deleteLabel?: string
  generateLabel?: string
  generatingLabel?: string
  dismissLabel?: string
  editLabel?: string
  markKeyPointLabel?: string
  unmarkKeyPointLabel?: string
  proHeader?: string
  conHeader?: string
  addProPlaceholder?: string
  addConPlaceholder?: string
}

export function OptionCard({
  id,
  label,
  proReasons,
  conReasons,
  isTouchDevice = false,
  autoFocusPro = false,
  isGenerating = false,
  generationError,
  onDismissError,
  onGenerate,
  showGenerateButton = false,
  isGenerateDisabled = false,
  onEditTitle,
  onDeleteOption,
  onAddReason,
  onEditReason,
  onDeleteReason,
  onToggleReasonWeight,
  titlePlaceholder = 'Option title…',
  deleteLabel = 'Delete option',
  generateLabel = 'Generate',
  generatingLabel = 'Generating…',
  dismissLabel = 'Dismiss',
  editLabel,
  markKeyPointLabel,
  unmarkKeyPointLabel,
  proHeader,
  conHeader,
  addProPlaceholder,
  addConPlaceholder,
}: OptionCardProps) {
  const proWeight = proReasons.reduce((s, r) => s + r.weight + 1, 0)
  const conWeight = conReasons.reduce((s, r) => s + r.weight + 1, 0)
  const totalWeight = proWeight + conWeight
  const proPct =
    totalWeight === 0 ? 50 : Math.round((proWeight / totalWeight) * 100)

  function handleTitleBlur(e: React.FocusEvent<HTMLInputElement>) {
    const trimmed = e.currentTarget.value.trim()
    if (trimmed === '') {
      onDeleteOption(id)
    } else if (trimmed !== label) {
      onEditTitle(id, trimmed)
    }
  }

  function handleTitleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') e.currentTarget.blur()
    if (e.key === 'Escape') {
      e.currentTarget.value = label
      e.currentTarget.blur()
    }
  }

  return (
    <>
      <div
        className="flex items-start gap-2 px-4 pt-4 pb-3"
        style={{ borderBottom: '1.5px dashed rgba(128,128,128,0.25)' }}
      >
        <input
          type="text"
          data-test="option-heading"
          defaultValue={label}
          onBlur={handleTitleBlur}
          onKeyDown={handleTitleKeyDown}
          placeholder={titlePlaceholder}
          className="border-0 border-b-2 border-[rgba(128,128,128,0.3)] border-dashed pb-1 outline-none transition-[border-color,border-style,background,color] duration-200 hover:[border-bottom-color:rgba(128,128,128,0.6)] focus:border-solid focus:border-ink dark:focus:border-dark-ink focus:bg-[rgba(128,128,128,0.03)] placeholder:text-[rgba(128,128,128,0.35)] dark:placeholder:text-[#908c87] flex-1 bg-transparent text-ink dark:text-dark-ink"
          style={{
            fontFamily: 'var(--zine-sans)',
            fontSize: '1.3rem',
            fontWeight: 800,
            letterSpacing: '-0.02em',
            lineHeight: 1.05,
          }}
        />
        <button
          data-test="option-delete-button"
          onClick={() => onDeleteOption(id)}
          aria-label={deleteLabel}
          className={`text-ink-secondary dark:text-dark-ink2 transition-opacity shrink-0 mt-1 bg-transparent border-0 cursor-pointer p-1 -mr-1 rounded ${isTouchDevice ? 'opacity-60' : 'opacity-30 hover:opacity-80'}`}
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      <BalanceBar
        proPct={proPct}
        isEmpty={totalWeight === 0}
        proLabel={proHeader}
        conLabel={conHeader}
      />

      {generationError && (
        <Transition
          as={Fragment}
          appear
          show
          enter="transition-opacity duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
        >
          <div
            className="flex items-center gap-2 px-4 py-3 mt-1 mb-0"
            style={{
              backgroundColor: 'var(--zine-con-bg)',
              borderBottom: '2px solid var(--zine-con)',
              color: 'var(--zine-con)',
            }}
            role="alert"
          >
            <ExclamationTriangleIcon className="h-4 w-4 shrink-0" />
            <span className="flex-1 text-xs font-bold tracking-wider">
              {generationError}
            </span>
            {onDismissError && (
              <button
                onClick={onDismissError}
                aria-label={dismissLabel}
                className="shrink-0 text-xs font-bold tracking-widest uppercase px-2.5 py-1 hover:opacity-70 transition-opacity"
                style={{ border: '1.5px solid var(--zine-con)' }}
              >
                {dismissLabel}
              </button>
            )}
          </div>
        </Transition>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 border-t-[2px] border-ink dark:border-dark-border">
        <div className="border-b-[2.5px] border-ink dark:border-dark-border md:border-b-0 md:border-r-[2.5px]">
          <ProConList
            side="PRO"
            items={proReasons}
            onAdd={(lbl) => onAddReason(id, 'PRO', lbl)}
            onEdit={onEditReason}
            onDelete={onDeleteReason}
            onToggleWeight={onToggleReasonWeight}
            isTouchDevice={isTouchDevice}
            autoFocus={autoFocusPro}
            proHeader={proHeader}
            addPlaceholder={addProPlaceholder}
            editLabel={editLabel}
            markKeyPointLabel={markKeyPointLabel}
            unmarkKeyPointLabel={unmarkKeyPointLabel}
          />
        </div>
        <div>
          <ProConList
            side="CON"
            items={conReasons}
            onAdd={(lbl) => onAddReason(id, 'CON', lbl)}
            onEdit={onEditReason}
            onDelete={onDeleteReason}
            onToggleWeight={onToggleReasonWeight}
            isTouchDevice={isTouchDevice}
            conHeader={conHeader}
            addPlaceholder={addConPlaceholder}
            editLabel={editLabel}
            markKeyPointLabel={markKeyPointLabel}
            unmarkKeyPointLabel={unmarkKeyPointLabel}
          />
        </div>
      </div>

      {showGenerateButton && onGenerate && (
        <div className="px-4 py-2 flex justify-end border-t border-[rgba(128,128,128,0.1)]">
          <GenerateButton
            onClick={onGenerate}
            disabled={isGenerateDisabled}
            isGenerating={isGenerating}
            generateLabel={generateLabel}
            generatingLabel={generatingLabel}
            className="text-xs font-bold tracking-widest uppercase text-ink-secondary dark:text-dark-ink2 hover:text-ink dark:hover:text-dark-ink disabled:opacity-40 transition-colors"
          />
        </div>
      )}
    </>
  )
}
